import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import {
  listOrPrompt,
  paragraphsOrPrompt,
  parseWholeCount,
  splitItems,
  splitLeadIn,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A graduate statement of purpose as an outline: five sections, a word target for each
 * taken from a typical split of the total, the writer's own points placed in them, and
 * prompts for whatever is still missing.
 *
 * The split is a planning aid, not a standard — programmes set their own prompts and
 * limits, and the programme's instructions always win. A research degree puts more
 * weight on research interests; a taught master's puts more on preparation and goals.
 */

export const SOP_LEVELS = ["research", "masters"] as const;
export type SopLevel = (typeof SOP_LEVELS)[number];

export const SOP_SECTIONS = [
  "motivation",
  "interests",
  "preparation",
  "fit",
  "goals",
] as const;
export type SopSection = (typeof SOP_SECTIONS)[number];

export const SOP_SHARES: Record<SopLevel, Record<SopSection, number>> = {
  research: {
    motivation: 10,
    interests: 30,
    preparation: 25,
    fit: 25,
    goals: 10,
  },
  masters: {
    motivation: 15,
    interests: 20,
    preparation: 30,
    fit: 20,
    goals: 15,
  },
};

export const SOP_DEFAULT_WORDS = 800;
export const SOP_MIN_WORDS = 200;
export const SOP_MAX_WORDS = 3000;

export interface StatementOfPurposeInput {
  level: SopLevel;
  /** The degree and school, for the title: "PhD in Marine Science, University of Lagos". */
  program: string;
  /** Target length in words. */
  words: string;
  motivation: string;
  interests: string;
  /** One experience, skill or course per line. */
  preparation: string;
  /** One faculty member per line: "Name — what connects". */
  faculty: string;
  /** One resource per line: a lab, a course, a data set, a centre. */
  resources: string;
  goals: string;
}

export interface StatementOfPurposeLabels {
  defaultTitle: string;
  subtitles: Record<SopLevel, string>;
  subtitleWords: string;
  budgetHeading: string;
  budgetNote: string;
  budget: { section: string; share: string; words: string; total: string };
  sections: Record<SopSection, string>;
  target: string;
  targetShare: string;
  tips: Record<SopSection, string>;
  subheadings: { faculty: string; resources: string };
  prompts: Record<SopSection | "faculty" | "resources", string>;
  facultyHead: { name: string; connection: string };
  checklistHeading: string;
  checklist: readonly string[];
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const PERCENT: Intl.NumberFormatOptions = { style: "percent" };

/** The target length, or null when it is missing or outside a sensible range. */
export function sopWordTotal(raw: string): number | null {
  const total = parseWholeCount(raw);
  if (total === null || total < SOP_MIN_WORDS || total > SOP_MAX_WORDS) {
    return null;
  }
  return total;
}

/** Word targets per section, in fives, adding up to the total. */
export function sopTargets(
  total: number,
  level: SopLevel
): Record<SopSection, number> {
  const words = allocateWords(
    total,
    SOP_SECTIONS.map((section) => SOP_SHARES[level][section]),
    5
  );
  const targets = {} as Record<SopSection, number>;
  SOP_SECTIONS.forEach((section, index) => {
    targets[section] = words[index];
  });
  return targets;
}

/** "Dr. Amina Yusuf — her reef-monitoring work" → ["Dr. Amina Yusuf", "her reef-monitoring work"]. */
export function parseFaculty(text: string): [string, string][] {
  return splitItems(text).map((line) => splitLeadIn(line) ?? [line, ""]);
}

function budgetBlock(
  labels: StatementOfPurposeLabels,
  level: SopLevel,
  targets: Record<SopSection, number> | null,
  fmt: Format
): DocBlock {
  const rows = SOP_SECTIONS.map((section) => {
    const row = [
      labels.sections[section],
      fmt(SOP_SHARES[level][section] / 100, PERCENT),
    ];
    return targets ? [...row, fmt(targets[section])] : row;
  });
  const sum = targets
    ? Object.values(targets).reduce((total, value) => total + value, 0)
    : 0;
  const totalRow = [labels.budget.total, fmt(1, PERCENT)];
  const head = [labels.budget.section, labels.budget.share];
  return {
    kind: "table",
    head: targets ? [...head, labels.budget.words] : head,
    rows: [...rows, targets ? [...totalRow, fmt(sum)] : totalRow],
  };
}

function guidance(
  section: SopSection,
  labels: StatementOfPurposeLabels,
  level: SopLevel,
  targets: Record<SopSection, number> | null,
  fmt: Format
): DocBlock {
  const share = fmt(SOP_SHARES[level][section] / 100, PERCENT);
  const target = targets
    ? t(labels.target, { words: fmt(targets[section]), share })
    : t(labels.targetShare, { share });
  return { kind: "guidance", text: `${target} ${labels.tips[section]}` };
}

function fitBlocks(
  input: StatementOfPurposeInput,
  labels: StatementOfPurposeLabels
): DocBlock[] {
  const faculty = parseFaculty(input.faculty);
  const facultyBlocks: DocBlock[] =
    faculty.length === 0
      ? [{ kind: "prompt", text: labels.prompts.faculty }]
      : [
          {
            kind: "table",
            head: [labels.facultyHead.name, labels.facultyHead.connection],
            rows: faculty,
          },
        ];
  return [
    { kind: "subheading", text: labels.subheadings.faculty },
    ...facultyBlocks,
    { kind: "subheading", text: labels.subheadings.resources },
    ...listOrPrompt(input.resources, "bullet", labels.prompts.resources),
  ];
}

function body(
  section: SopSection,
  input: StatementOfPurposeInput,
  labels: StatementOfPurposeLabels
): DocBlock[] {
  switch (section) {
    case "motivation":
      return paragraphsOrPrompt(input.motivation, labels.prompts.motivation);
    case "interests":
      return paragraphsOrPrompt(input.interests, labels.prompts.interests);
    case "preparation":
      return listOrPrompt(
        input.preparation,
        "bullet",
        labels.prompts.preparation
      );
    case "fit":
      return fitBlocks(input, labels);
    default:
      return paragraphsOrPrompt(input.goals, labels.prompts.goals);
  }
}

export function buildStatementOfPurpose(
  input: StatementOfPurposeInput,
  labels: StatementOfPurposeLabels,
  fmt: Format
): DocumentModel {
  const total = sopWordTotal(input.words);
  const targets = total ? sopTargets(total, input.level) : null;
  const sections: DocSection[] = SOP_SECTIONS.map((section) => ({
    heading: labels.sections[section],
    blocks: [
      guidance(section, labels, input.level, targets, fmt),
      ...body(section, input, labels),
    ],
  }));
  const subtitle = labels.subtitles[input.level];
  return {
    title: input.program.trim() || labels.defaultTitle,
    subtitle: total
      ? t(labels.subtitleWords, { level: subtitle, words: fmt(total) })
      : subtitle,
    sections: [
      {
        heading: labels.budgetHeading,
        blocks: [
          { kind: "guidance", text: labels.budgetNote },
          budgetBlock(labels, input.level, targets, fmt),
        ],
      },
      ...sections,
      {
        heading: labels.checklistHeading,
        blocks: [
          {
            kind: "checklist",
            items: labels.checklist.map((text) => ({ text })),
          },
        ],
      },
    ],
  };
}
